import { useEffect, useMemo, useState } from 'react'
import { AdminLayout, useAdminAccess } from '../../components/AdminLayout'
import { useLanguage } from '../../contexts/LanguageContext'
import { requestApi } from '../../lib/adminApi'

type ChannelRuntime = {
  status?: string
  priority?: number
  enabled?: boolean
  is_official?: boolean
  available?: boolean
  fail_count?: number
  total_requests?: number
  total_failures?: number
  success_rate?: string
  avg_latency_ms?: number
}

type ChannelStatsResponse = {
  current?: string | null
  current_tier?: string | null
  channels?: Record<string, ChannelRuntime>
}

type ChannelMetric = {
  total_requests: number
  total_failures: number
  success_rate: string
  avg_latency_ms: number
}

type ChannelMetricsResponse = {
  hours: number
  channels: Record<string, ChannelMetric>
}

type ChannelTrendItem = {
  bucket: string
  total_requests: number
  total_failures: number
  avg_latency_ms: number
}

type ChannelTrendResponse = {
  hours: number
  data: ChannelTrendItem[]
}

type ChannelErrorItem = {
  channel_name: string
  error_type?: string | null
  error_message?: string | null
  model?: string | null
  provider?: string | null
  created_at?: string | null
}

type ChannelErrorsResponse = {
  errors: ChannelErrorItem[]
}

export const buildMetricsSummary = (metrics: Record<string, ChannelMetric>) => {
  const items = Object.values(metrics || {})
  const totalRequests = items.reduce((sum, item) => sum + (item.total_requests || 0), 0)
  const totalFailures = items.reduce((sum, item) => sum + (item.total_failures || 0), 0)
  const weightedLatency = items.reduce(
    (sum, item) => sum + (item.avg_latency_ms || 0) * (item.total_requests || 0),
    0
  )
  const avgLatencyMs = totalRequests ? Math.round(weightedLatency / totalRequests) : 0
  const failureRate = totalRequests ? totalFailures / totalRequests : 0

  return {
    totalRequests,
    totalFailures,
    avgLatencyMs,
    failureRate
  }
}

const AdminAIChannelMonitorContent = () => {
  const { t } = useLanguage()
  const { isGranted } = useAdminAccess()
  const [stats, setStats] = useState<ChannelStatsResponse | null>(null)
  const [metrics, setMetrics] = useState<ChannelMetricsResponse | null>(null)
  const [trend, setTrend] = useState<ChannelTrendResponse | null>(null)
  const [errors, setErrors] = useState<ChannelErrorItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [lastUpdated, setLastUpdated] = useState<string>('')

  const fetchStats = () => {
    setLoading(true)
    setError('')
    Promise.all([
      requestApi<ChannelStatsResponse>('/api/v1/admin/ai-channels/stats'),
      requestApi<ChannelMetricsResponse>('/api/v1/admin/ai-channels/metrics'),
      requestApi<ChannelTrendResponse>('/api/v1/admin/ai-channels/trend?hours=24'),
      requestApi<ChannelErrorsResponse>('/api/v1/admin/ai-channels/errors?limit=20')
    ])
      .then(([statsPayload, metricsPayload, trendPayload, errorsPayload]) => {
        setStats(statsPayload)
        setMetrics(metricsPayload)
        setTrend(trendPayload)
        setErrors(errorsPayload.errors || [])
        setLastUpdated(new Date().toLocaleTimeString())
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('admin.aiMonitor.error'))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (!isGranted) return
    fetchStats()
  }, [isGranted])

  useEffect(() => {
    if (!autoRefresh || !isGranted) return
    const timer = window.setInterval(() => {
      fetchStats()
    }, 30000)
    return () => window.clearInterval(timer)
  }, [autoRefresh, isGranted])

  const channels = useMemo(() => {
    const runtimeMap = stats?.channels || {}
    const metricMap = metrics?.channels || {}
    const names = new Set<string>([...Object.keys(runtimeMap), ...Object.keys(metricMap)])
    return Array.from(names).map((name) => ({
      name,
      ...(runtimeMap[name] || {}),
      ...(metricMap[name] || {})
    }))
  }, [stats, metrics])

  const summary = useMemo(() => {
    const total = channels.length
    const enabled = channels.filter((ch) => ch.enabled).length
    const healthy = channels.filter((ch) => ch.status === 'healthy').length
    const metricsSummary = buildMetricsSummary(metrics?.channels || {})
    return {
      total,
      enabled,
      healthy,
      metricsSummary
    }
  }, [channels])

  const statusClass = (status?: string) => {
    if (status === 'healthy') return 'bg-emerald-100 text-emerald-700'
    if (status === 'degraded') return 'bg-amber-100 text-amber-700'
    if (status === 'failed') return 'bg-rose-100 text-rose-700'
    if (status === 'disabled') return 'bg-slate-200 text-slate-500'
    return 'bg-slate-100 text-slate-500'
  }

  const trendData = trend?.data || []
  const maxRequests = Math.max(1, ...trendData.map((item) => item.total_requests || 0))

  const formatBucket = (bucket: string) => {
    if (!bucket) return '-'
    if (bucket.includes('T')) {
      return bucket.split('T')[1]?.slice(0, 5) || bucket
    }
    return bucket.slice(-5)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        {lastUpdated && (
          <p className="text-xs text-slate-400">{t('admin.aiMonitor.updatedAt').replace('{time}', lastUpdated)}</p>
        )}
        <div className="flex items-center gap-3">
          <button
            onClick={fetchStats}
            className="px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
          >
            {t('admin.aiMonitor.refresh')}
          </button>
          <label className="flex items-center gap-2 text-xs text-slate-500">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(event) => setAutoRefresh(event.target.checked)}
            />
            {t('admin.aiMonitor.autoRefresh')}
          </label>
        </div>
      </div>
        {loading && <p className="text-sm text-slate-400">{t('admin.aiMonitor.loading')}</p>}
        {error && <p className="text-sm text-rose-500">{error}</p>}
        {!loading && !error && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50/70">
                <p className="text-xs uppercase tracking-widest text-slate-400">{t('admin.aiMonitor.current')}</p>
                <p className="text-lg font-semibold text-slate-800 mt-2">{stats?.current || t('admin.aiMonitor.none')}</p>
              </div>
              <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50/70">
                <p className="text-xs uppercase tracking-widest text-slate-400">{t('admin.aiMonitor.total')}</p>
                <p className="text-lg font-semibold text-slate-800 mt-2">{summary.total}</p>
              </div>
              <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50/70">
                <p className="text-xs uppercase tracking-widest text-slate-400">{t('admin.aiMonitor.healthy')}</p>
                <p className="text-lg font-semibold text-slate-800 mt-2">{summary.healthy}</p>
              </div>
              <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50/70">
                <p className="text-xs uppercase tracking-widest text-slate-400">{t('admin.aiMonitor.requests')}</p>
                <p className="text-lg font-semibold text-slate-800 mt-2">{summary.metricsSummary.totalRequests}</p>
              </div>
              <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50/70">
                <p className="text-xs uppercase tracking-widest text-slate-400">{t('admin.aiMonitor.failureRate')}</p>
                <p className="text-lg font-semibold text-slate-800 mt-2">
                  {(summary.metricsSummary.failureRate * 100).toFixed(1)}%
                </p>
              </div>
              <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50/70">
                <p className="text-xs uppercase tracking-widest text-slate-400">{t('admin.aiMonitor.avgLatency')}</p>
                <p className="text-lg font-semibold text-slate-800 mt-2">{summary.metricsSummary.avgLatencyMs} ms</p>
              </div>
            </div>

            <div className="border border-slate-200 rounded-2xl p-4 bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-700">{t('admin.aiMonitor.trend')}</h3>
                <span className="text-xs text-slate-400">{t('admin.aiMonitor.last24h')}</span>
              </div>
              <div className="flex items-end gap-2 h-32">
                {trendData.map((item) => (
                  <div key={item.bucket} className="flex flex-col items-center flex-1">
                    <div
                      className="w-full rounded-t-lg bg-blue-200"
                      style={{ height: `${Math.max(8, (item.total_requests / maxRequests) * 100)}%` }}
                    />
                    <span className="text-[10px] text-slate-400 mt-2">{formatBucket(item.bucket)}</span>
                  </div>
                ))}
                {trendData.length === 0 && (
                  <div className="text-xs text-slate-400">{t('admin.aiMonitor.noTrend')}</div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto border border-slate-200 rounded-2xl">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-400 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">{t('admin.aiMonitor.table.name')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.aiMonitor.table.status')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.aiMonitor.table.priority')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.aiMonitor.table.enabled')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.aiMonitor.table.available')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.aiMonitor.table.successRate')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.aiMonitor.table.failCount')}</th>
                    <th className="px-4 py-3 text-left">{t('admin.aiMonitor.table.avgLatency')}</th>
                  </tr>
                </thead>
                <tbody>
                  {channels.map((channel) => (
                    <tr key={channel.name} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-semibold text-slate-700">{channel.name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusClass(channel.status)}`}>
                          {channel.status || t('admin.aiMonitor.statusUnknown')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{channel.priority ?? '-'}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {channel.enabled ? t('admin.aiMonitor.yes') : t('admin.aiMonitor.no')}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {channel.available ? t('admin.aiMonitor.yes') : t('admin.aiMonitor.no')}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{channel.success_rate || '-'}</td>
                      <td className="px-4 py-3 text-slate-600">{channel.fail_count ?? 0}</td>
                      <td className="px-4 py-3 text-slate-600">{channel.avg_latency_ms ?? '-'}</td>
                    </tr>
                  ))}
                  {channels.length === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-center text-sm text-slate-400" colSpan={8}>
                        {t('admin.aiMonitor.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="border border-slate-200 rounded-2xl p-4 bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-700">{t('admin.aiMonitor.errors')}</h3>
                <span className="text-xs text-slate-400">{t('admin.aiMonitor.errorsDesc')}</span>
              </div>
              {errors.length === 0 ? (
                <p className="text-xs text-slate-400">{t('admin.aiMonitor.noErrors')}</p>
              ) : (
                <div className="space-y-3">
                  {errors.map((item, index) => (
                    <div key={`${item.channel_name}-${index}`} className="border border-slate-100 rounded-xl p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-slate-700">{item.channel_name}</span>
                        <span className="text-[10px] text-slate-400">
                          {item.created_at ? new Date(item.created_at).toLocaleString() : '-'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        {(item.error_message || item.error_type || '-').slice(0, 120)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
  )
}

export const AdminAIChannelMonitorScreen = () => {
  const { t } = useLanguage()

  return (
    <AdminLayout title={t('admin.aiMonitor.title')} description={t('admin.aiMonitor.desc')}>
      <AdminAIChannelMonitorContent />
    </AdminLayout>
  )
}
